import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ShieldCheck, Sparkles, Zap, Wrench, Phone, Truck, ChevronRight, Star, Award, ChevronDown, Quote, LayoutDashboard, LogOut } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import PlanCard from "@/components/PlanCard";
import InstantQuote from "@/components/InstantQuote";
import TCOCalculator from "@/components/TCOCalculator";
import ReviewsFeed from "@/components/ReviewsFeed";
import LandingLeaderboard from "@/components/LandingLeaderboard";
import AppStoreBadges from "@/components/AppStoreBadges";
import { LANDING, NAV } from "@/constants/testIds";

const HERO = "https://images.unsplash.com/photo-1512428559087-560fa5ceab42";
const REPAIR = "https://images.unsplash.com/photo-1550041473-d296a3a8a18a";
const PREMIUM = "https://images.pexels.com/photos/36659012/pexels-photo-36659012.jpeg";

export default function Landing() {
  const [plans, setPlans] = useState([]);
  const [cycle, setCycle] = useState("monthly");
  const [stats, setStats] = useState(null);
  const nav = useNavigate();
  const { user, logout } = useAuth();

  useEffect(() => { api.get("/plans").then(r => setPlans(r.data)).catch(() => {}); }, []);
  useEffect(() => { api.get("/stats/public").then(r => setStats(r.data)).catch(() => {}); }, []);

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Nav */}
      <header className="sticky top-0 z-30 glass">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid={NAV.home}>
            <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center">
              <ShieldCheck className="w-5 h-5 text-secondary" />
            </div>
            <div>
              <div className="font-display font-semibold text-lg tracking-tight leading-none">Treasurescare</div>
            </div>
          </Link>
          <nav className="hidden md:flex items-center gap-8 text-sm">
            <a href="#plans" className="text-foreground/70 hover:text-foreground transition-colors" data-testid={NAV.plans}>Plans</a>
            <a href="#how" className="text-foreground/70 hover:text-foreground transition-colors">How it works</a>
            <a href="#business" className="text-foreground/70 hover:text-foreground transition-colors">For business</a>
          </nav>
          <div className="flex items-center gap-2">
            {user ? (
              <>
                <Link to="/app" data-testid="nav-dashboard">
                  <Button size="sm" className="bg-primary text-primary-foreground"><LayoutDashboard className="w-3.5 h-3.5 mr-1.5" /> Dashboard</Button>
                </Link>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={async () => { await logout(); nav("/"); }}
                  data-testid="nav-signout"
                  title="Sign out"
                >
                  <LogOut className="w-4 h-4" />
                </Button>
              </>
            ) : (
              <>
                <Link to="/login" data-testid={NAV.login}>
                  <Button variant="ghost" size="sm">Sign in</Button>
                </Link>
                <Link to="/signup" data-testid={NAV.signup}>
                  <Button size="sm" className="bg-primary text-primary-foreground">Get started</Button>
                </Link>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 pt-16 lg:pt-24 pb-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 animate-fade-up">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/5 border border-primary/10 text-xs text-primary mb-6">
              <Sparkles className="w-3 h-3" /> Every gadget · one plan · £0 excess on standard repairs
            </div>
            <h1 className="font-display text-5xl lg:text-7xl font-light tracking-tighter leading-[1.02]">
              Your gadgets,<br /><span className="italic text-primary">treasured</span> and protected.
            </h1>
            <p className="mt-6 text-lg text-muted-foreground max-w-xl">
              Cover for phones, laptops, tablets, watches, headphones — every gadget your family owns, under one bill.
              From £4.99 a month, <strong className="text-primary">roughly half of AppleCare+</strong> — with zero excess on standard repairs.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Button
                data-testid="landing-get-quote"
                onClick={() => document.getElementById("instant-quote")?.scrollIntoView({ behavior: "smooth" })}
                size="lg"
                className="bg-primary text-primary-foreground rounded-full px-7"
              >
                Get instant quote <Zap className="w-4 h-4 ml-1" />
              </Button>
              <Button
                data-testid={LANDING.getStarted}
                onClick={() => nav("/signup")}
                variant="outline"
                size="lg"
                className="rounded-full px-7"
              >
                Get started <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
              <Button
                data-testid={LANDING.viewPlans}
                variant="ghost"
                size="lg"
                className="rounded-full px-4 text-foreground/70"
                onClick={() => document.getElementById("plans")?.scrollIntoView({ behavior: "smooth" })}
              >
                Compare plans
              </Button>
            </div>
            <div className="mt-8">
              <div className="text-[10px] overline text-muted-foreground mb-2">Now available on mobile</div>
              <AppStoreBadges />
            </div>
            <div className="mt-8 flex items-center gap-6 text-sm text-muted-foreground">
              <div className="flex items-center gap-2"><Star className="w-4 h-4 text-secondary fill-secondary" /> 4.9 / 5 avg. rating</div>
              <div className="flex items-center gap-2"><Award className="w-4 h-4 text-primary" /> 24h repair TAT</div>
            </div>
          </div>
          <div className="lg:col-span-5 relative">
            <div className="absolute -inset-4 bg-primary/5 rounded-3xl blur-2xl" />
            <div className="relative bg-card rounded-3xl overflow-hidden border border-border shadow-teal-glow">
              <img src={PREMIUM} alt="Premium device" className="w-full h-[420px] object-cover" />
              <div className="p-6 border-t border-border">
                <div className="overline text-muted-foreground">Live claim</div>
                <div className="mt-2 font-display text-xl">Case CASE-8F2A1B02 · <span className="text-primary">In transit</span></div>
                <div className="text-sm text-muted-foreground mt-1">iPhone 15 Pro · Cracked screen · Courier ETA 14:20</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Instant Quote Widget — Phase A */}
      <section id="instant-quote" className="max-w-7xl mx-auto px-6 lg:px-8 pb-20 border-t border-border pt-20">
        <InstantQuote />
      </section>

      {/* Live Stats Trust Badges — Phase C */}
      <section data-testid="trust-badges" className="max-w-7xl mx-auto px-6 lg:px-8 py-14 border-t border-border">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {[
            { label: "Devices protected", value: stats ? stats.devices_protected.toLocaleString() : "—", tid: "stats-devices" },
            { label: "Repairs covered", value: stats ? `£${Math.round(stats.repairs_covered_gbp).toLocaleString()}` : "—", tid: "stats-repairs" },
            { label: "Avg repair time", value: stats ? `${stats.avg_repair_hours}h` : "—", tid: "stats-tat" },
            { label: "Happy members", value: stats ? stats.members_count.toLocaleString() : "—", tid: "stats-members" },
          ].map((s) => (
            <div key={s.label} data-testid={s.tid} className="text-center">
              <div className="font-display text-4xl lg:text-5xl font-semibold text-primary">{s.value}</div>
              <div className="mt-2 text-xs overline text-muted-foreground">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Community Leaderboard — Gems social proof */}
      <LandingLeaderboard />

      {/* Free repair-cost estimator CTA — Phase C SEO / lead-gen tool */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 pb-20">
        <div data-testid="estimator-cta" className="bg-secondary/5 border border-secondary/30 rounded-3xl p-8 lg:p-10 flex flex-col md:flex-row md:items-center gap-6 justify-between">
          <div>
            <div className="overline text-secondary">Free tool</div>
            <h3 className="mt-2 font-display text-2xl lg:text-3xl">Curious what a repair would cost you?</h3>
            <p className="mt-1 text-sm text-muted-foreground">See the out-of-pocket price vs a year of Treasurescare cover — no signup needed.</p>
          </div>
          <Button
            className="bg-secondary text-teal hover:bg-secondary/90"
            data-testid="estimator-open-btn"
            onClick={() => nav("/repair-cost")}
          >
            Open repair calculator <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </section>

      {/* Total Cost of Ownership — attacks AppleCare / SamsungCare directly */}
      <section id="compare" className="max-w-7xl mx-auto px-6 lg:px-8 pb-20 border-t border-border pt-20">
        <TCOCalculator />
        <div className="mt-6 flex flex-wrap gap-3 items-center justify-center text-sm">
          <span className="text-muted-foreground">Go deeper:</span>
          <Link to="/vs-applecare" className="text-primary hover:underline font-medium" data-testid="landing-vs-applecare">
            Treasurescare vs AppleCare+ →
          </Link>
          <span className="text-muted-foreground">·</span>
          <Link to="/vs-samsungcare" className="text-primary hover:underline font-medium" data-testid="landing-vs-samsungcare">
            Treasurescare vs Samsung Care+ →
          </Link>
        </div>
      </section>

      {/* Real reviews — Phase D social proof */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 pb-20">
        <div className="text-center max-w-2xl mx-auto mb-10">
          <div className="overline text-secondary">Members love us</div>
          <h3 className="mt-2 font-display text-3xl lg:text-4xl font-light">Real stories, real repairs.</h3>
        </div>
        <ReviewsFeed />
      </section>

      {/* How it works */}
      <section id="how" className="max-w-7xl mx-auto px-6 lg:px-8 py-20 border-t border-border">
        <div className="overline text-secondary">How it works</div>
        <h2 className="mt-3 font-display text-4xl lg:text-5xl font-light tracking-tight max-w-2xl">
          Damaged phone? We handle everything.
        </h2>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-4 gap-6">
          {[
          { icon: Phone, title: "1. Register your phone", body: "Add IMEI, brand, model. Upload photo & proof of purchase." },
          { icon: ShieldCheck, title: "2. Pick your plan", body: "Finder (free), Seeker, Hunter, Vault or Household. Monthly, quarterly, or annual." },
          { icon: Zap, title: "3. File a claim", body: "AI validates damage from photos and estimates repair cost." },
          { icon: Wrench, title: "4. Repair & return", body: "Courier picks up, workshop repairs, tracked all the way back." },
          ].map((s) => {
            const Icon = s.icon;
            return (
              <div key={s.title} className="bg-card border border-border rounded-2xl p-6 hover:-translate-y-1 transition-transform">
                <div className="w-11 h-11 rounded-xl bg-primary/8 text-primary flex items-center justify-center">
                  <Icon className="w-5 h-5" />
                </div>
                <div className="mt-4 font-display font-semibold text-lg">{s.title}</div>
                <div className="mt-1 text-sm text-muted-foreground">{s.body}</div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Plans */}
      <section id="plans" className="max-w-7xl mx-auto px-6 lg:px-8 py-20 border-t border-border">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <div>
            <div className="overline text-secondary">Membership tiers</div>
            <h2 className="mt-3 font-display text-4xl lg:text-5xl font-light tracking-tight">Pick your protection.</h2>
            <p className="mt-3 text-muted-foreground max-w-xl">Cancel or upgrade anytime. No hidden excess fees on standard repairs.</p>
          </div>
          <div className="inline-flex bg-muted rounded-full p-1">
            {["monthly", "quarterly", "annual"].map(c => (
              <button
                key={c}
                onClick={() => setCycle(c)}
                data-testid={`cycle-${c}`}
                className={`px-4 py-1.5 text-sm rounded-full transition-colors ${cycle === c ? "bg-primary text-primary-foreground" : "text-foreground/70"}`}
              >
                {c[0].toUpperCase() + c.slice(1)}
                {c === "quarterly" && <span className="ml-1 text-[10px] text-secondary font-semibold">−5%</span>}
                {c === "annual" && <span className="ml-1 text-[10px] text-secondary font-semibold">−15%</span>}
              </button>
            ))}
          </div>
        </div>
        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {plans.map(p => (
            <PlanCard key={p.id} plan={p} cycle={cycle} ctaLabel="Get started" onSubscribe={() => nav("/signup")} />
          ))}
        </div>
      </section>

      {/* Repair banner */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 py-20 border-t border-border">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div className="rounded-3xl overflow-hidden border border-border">
            <img src={REPAIR} alt="Repair" className="w-full h-[420px] object-cover" />
          </div>
          <div>
            <div className="overline text-secondary">AI damage assessment</div>
            <h2 className="mt-3 font-display text-4xl lg:text-5xl font-light tracking-tight">
              Snap it. Send it. <span className="text-primary italic">Sorted.</span>
            </h2>
            <p className="mt-4 text-muted-foreground max-w-lg">
              Gemini-powered vision analyses your damage photos, estimates the repair cost and flags anomalies —
              so genuine claims fly through, and repairs start the same day.
            </p>
            <ul className="mt-6 space-y-3 text-sm">
              <li className="flex gap-3"><Sparkles className="w-4 h-4 text-secondary shrink-0 mt-0.5" /> Severity + repair estimate in under 10 seconds</li>
              <li className="flex gap-3"><Truck className="w-4 h-4 text-secondary shrink-0 mt-0.5" /> Auto-generated courier label — Royal Mail Tracked 24</li>
              <li className="flex gap-3"><ShieldCheck className="w-4 h-4 text-secondary shrink-0 mt-0.5" /> Live status timeline for every step</li>
            </ul>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="border-t border-border">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20">
          <div className="overline text-secondary">Loved by 2,400+ members</div>
          <h2 className="mt-3 font-display text-4xl lg:text-5xl font-light tracking-tight max-w-2xl">
            Real stories, real repairs.
          </h2>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { name: "Priya S.", tier: "Vault", body: "AI validated my cracked screen in 8 seconds. Courier next morning, phone back in 24h. This is what care should feel like." },
              { name: "Marcus B.", tier: "Hunter", body: "Water-damaged phone during rain. Filed the claim in three taps on the tube. Peace of mind for a fiver a month." },
              { name: "Nina K., ACME Ltd.", tier: "Business", body: "Bulk-imported 84 employee phones with one CSV. Central billing, single dashboard. Saved us hours." },
            ].map((t) => (
              <div key={t.name} className="bg-card border border-border rounded-2xl p-7">
                <div className="flex gap-0.5 text-secondary">{[0,1,2,3,4].map(idx => <Star key={`${t.name}-star-${idx}`} className="w-4 h-4 fill-secondary" />)}</div>
                <div className="mt-4 text-foreground/85 leading-relaxed">"{t.body}"</div>
                <div className="mt-5 flex items-center justify-between text-sm">
                  <span className="font-medium">{t.name}</span>
                  <span className="text-[10px] overline text-muted-foreground">{t.tier}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="border-t border-border">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 py-20">
          <div className="overline text-secondary">FAQ</div>
          <h2 className="mt-3 font-display text-4xl lg:text-5xl font-light tracking-tight">Questions? Answered.</h2>
          <div className="mt-10 divide-y divide-border border-y border-border">
            {[
              { q: "Which phones are eligible?", a: "Any smartphone (iPhone, Samsung, Pixel, and more) purchased in the UK, up to 24 months old at the time of registration. We verify eligibility using IMEI + proof of purchase." },
              { q: "How fast do claims get processed?", a: "AI validates most claims in under 10 seconds. Courier pickup is next-working-day standard, same-day for Vault. Repairs are typically completed within 24 hours." },
              { q: "Is there an excess fee?", a: "No excess on standard screen or battery repairs from Hunter tier up. Water damage on Hunter+ includes a £25 admin fee. Vault and Household have £0 excess on everything." },
              { q: "Can I cancel anytime?", a: "Yes — cancel in one click from your dashboard. No lock-in on monthly plans. Annual plans get a pro-rated refund of unused months." },
              { q: "Do you cover devices bought abroad?", a: "Not yet — Treasurescare is UK-only for now. Multi-country roll-out is on the roadmap." },
              { q: "How is my data protected?", a: "All data is stored in UK-based, GDPR-compliant infrastructure. Identity documents are encrypted at rest. See our privacy policy for details." },
            ].map((f) => (
              <details key={f.q} className="group py-5">
                <summary className="flex justify-between items-center cursor-pointer list-none">
                  <span className="font-medium">{f.q}</span>
                  <span className="text-primary transition-transform group-open:rotate-45 text-2xl leading-none">+</span>
                </summary>
                <div className="mt-3 text-sm text-muted-foreground">{f.a}</div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* B2B */}
      <section id="business" className="border-t border-border">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20">
          <div className="bg-primary text-primary-foreground rounded-3xl p-10 lg:p-14 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            <div>
              <div className="overline text-secondary">For business</div>
              <h2 className="mt-3 font-display text-4xl lg:text-5xl font-light tracking-tight">
                Protect every phone across your workforce.
              </h2>
              <p className="mt-4 text-primary-foreground/70 max-w-lg">
                Register hundreds of devices via CSV. Central billing. Agents can file
                claims for employees. Real-time portfolio dashboards.
              </p>
              <div className="mt-6 flex gap-3">
                <Button className="bg-secondary text-teal hover:bg-secondary/90" onClick={() => nav("/signup?type=b2b")}>
                  Set up your organisation
                </Button>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {[
                { k: "Bulk import", v: "CSV upload for 1000+ devices" },
                { k: "Central billing", v: "One invoice, all employees" },
                { k: "Agent claims", v: "Raise on behalf of staff" },
                { k: "Audit trail", v: "GDPR-ready compliance logs" },
              ].map((f) => (
                <div key={f.k} className="bg-white/5 backdrop-blur border border-white/10 rounded-xl p-4">
                  <div className="text-secondary text-xs overline">{f.k}</div>
                  <div className="mt-1 text-sm">{f.v}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 py-20 border-t border-border">
        <div className="text-center mb-12">
          <div className="overline text-secondary">Trusted by 12,000+ customers</div>
          <h2 className="mt-3 font-display text-4xl lg:text-5xl font-light tracking-tight">Real stories, real repairs.</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="landing-testimonials">
          {[
            {
              name: "Priya S.",
              role: "iPhone 14 · Vault plan",
              body: "Cracked screen on Monday, phone back on Tuesday afternoon. Free courier collection, no drama.",
              rating: 5,
            },
            {
              name: "Marcus L.",
              role: "Galaxy S23 · Hunter plan",
              body: "Battery replacement was seamless. The chatbot even helped me diagnose the issue before I filed the claim.",
              rating: 5,
            },
            {
              name: "ACME Ltd. Ops",
              role: "B2B · 42 devices",
              body: "Onboarded our whole sales team via CSV in ten minutes. Central billing means finance loves it too.",
              rating: 5,
            },
          ].map((t) => (
            <div key={t.name} className="rounded-2xl bg-card border border-border p-6 relative">
              <Quote className="w-8 h-8 text-primary/20 absolute top-4 right-4" />
              <div className="flex gap-1 text-amber-400 mb-3">
                {Array.from({ length: t.rating }).map((_, i) => (
                  <Star key={i} className="w-4 h-4 fill-current" />
                ))}
              </div>
              <p className="text-sm leading-relaxed">{t.body}</p>
              <div className="mt-4 pt-4 border-t border-border">
                <div className="font-semibold text-sm">{t.name}</div>
                <div className="text-xs text-muted-foreground">{t.role}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-3xl mx-auto px-6 lg:px-8 py-20 border-t border-border">
        <div className="text-center mb-10">
          <div className="overline text-secondary">FAQ</div>
          <h2 className="mt-3 font-display text-4xl font-light tracking-tight">Everything you need to know</h2>
        </div>
        <div className="space-y-3" data-testid="landing-faq">
          {[
            {
              q: "How fast can you repair my phone?",
              a: "Vault customers get same-day service where a repair centre is within 25 miles. Hunter: 24 hours. Seeker: 48 hours. Finder gets standard 5-day workshop TAT. All timings from device receipt.",
            },
            {
              q: "Does the cover apply if I'm travelling?",
              a: "Yes on Household. Hunter and Vault cover the UK by default; add the International Cover rider for global protection at £2/mo.",
            },
            {
              q: "Can I add more than one device?",
              a: "Absolutely. Each device gets its own membership. Family or business bundles are available on request.",
            },
            {
              q: "What about theft?",
              a: "Vault and Household plans include theft and loss with a valid police report reference. Finder, Seeker, and Hunter plans do not include theft.",
            },
            {
              q: "How do I cancel?",
              a: "Cancel any time from your Subscription page. No lock-in, no cancellation fee. Refunds are pro-rated to the day.",
            },
            {
              q: "Is my data secure?",
              a: "Treasurescare is GDPR compliant and UK-registered. Photos and IMEIs are encrypted at rest. We never sell your data.",
            },
          ].map((item, idx) => (
            <details key={item.q} className="group rounded-xl border border-border bg-card p-5" data-testid={`faq-item-${idx}`}>
              <summary className="flex items-center justify-between cursor-pointer list-none">
                <span className="font-medium">{item.q}</span>
                <ChevronDown className="w-4 h-4 text-muted-foreground transition-transform group-open:rotate-180" />
              </summary>
              <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{item.a}</p>
            </details>
          ))}
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-10 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center"><ShieldCheck className="w-4 h-4 text-secondary" /></div>
            <div>
              <div className="font-display font-semibold">Treasurescare</div>
              <div className="text-[10px] overline text-muted-foreground">© SRAM & MRAM LIMITED · UK</div>
            </div>
          </div>
          <AppStoreBadges variant="compact" />
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-muted-foreground">
            <Link to="/about" className="hover:text-foreground transition-colors" data-testid="footer-about">About</Link>
            <Link to="/blog" className="hover:text-foreground transition-colors" data-testid="footer-blog">Blog</Link>
            <Link to="/news" className="hover:text-foreground transition-colors" data-testid="footer-news">News</Link>
            <Link to="/contact" className="hover:text-foreground transition-colors" data-testid="footer-contact">Contact</Link>
          </div>
          <div className="text-xs text-muted-foreground">GDPR compliant · UK-registered · <a href="mailto:info@treasurescare.com" className="hover:text-foreground">info@treasurescare.com</a></div>
        </div>
      </footer>
    </div>
  );
}
